import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { History, Upload, CheckCircle2, Undo2 } from 'lucide-react';
import ax from '@/lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const HistoryImport = ({ venues }) => {
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [imports, setImports] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const pendingFile = useRef(null);

  const fetchImports = useCallback(async () => {
    try { const { data } = await ax.get(`${API}/trivia/history/imports`); setImports(data.imports); } catch {}
  }, []);
  useEffect(() => { fetchImports(); }, [fetchImports]);

  const runPreview = async (file) => {
    setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file, file.name);
      const { data } = await ax.post(`${API}/trivia/history/preview`, form);
      pendingFile.current = file;
      setPreview(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read that file');
    } finally { setBusy(false); }
  };

  const mapVenue = async (oldName, venueId) => {
    try {
      await ax.post(`${API}/trivia/history/map`, { old_name: oldName, venue_id: venueId || null });
      toast.success(venueId ? 'Mapped' : 'Venue will be skipped');
      if (pendingFile.current) runPreview(pendingFile.current);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const confirm = async () => {
    if (!pendingFile.current) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', pendingFile.current, pendingFile.current.name);
      const { data } = await ax.post(`${API}/trivia/history/confirm`, form);
      setResult(data); setPreview(null); pendingFile.current = null;
      toast.success(`${data.written} history rows written, ${data.locks_applied} rounds locked`);
      fetchImports();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  const undo = async (id) => {
    if (!window.confirm('Undo this import? Only what it wrote is removed.')) return;
    try {
      const { data } = await ax.delete(`${API}/trivia/history/import/${id}`);
      toast.success(`${data.history_rows_removed} history rows and ${data.locks_removed} locks removed`);
      setResult(null); fetchImports();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const unmatched = preview?.unmatched || [];

  return (
    <Card className="glass-panel border-0" data-testid="history-import-card">
      <CardHeader>
        <CardTitle className="text-white text-lg flex items-center gap-2">
          <History className="w-5 h-5 text-[#fbdd68]" /> Import old round history
        </CardTitle>
        <CardDescription className="text-[#8892b0]">
          Upload a PC's round_usage file (.txt or .json). You get a preview first — nothing is saved until you confirm.
          Still-active uses lock their rounds at the matched venue and reach every linked PC.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <input ref={fileRef} type="file" accept=".txt,.json,application/json,text/plain" className="hidden"
            onChange={(e) => { if (e.target.files[0]) runPreview(e.target.files[0]); e.target.value = ''; }} />
          <Button data-testid="history-pick-file" onClick={() => fileRef.current?.click()} disabled={busy}
            className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685] h-9 text-xs">
            <Upload className="w-3.5 h-3.5 mr-1.5" />{busy ? 'Working…' : 'Choose history file'}
          </Button>
        </div>

        {preview && (
          <div data-testid="history-preview" className="rounded-lg bg-[rgba(20,27,80,0.5)] border border-[rgba(251,221,104,0.1)] p-4 space-y-3">
            <p className="text-sm font-bold text-white">Preview — nothing saved yet</p>
            <p className="text-xs text-[#c8d0e8]" data-testid="history-preview-stats">
              {preview.rows} rows · {preview.venues_found} venues found · {preview.matched_venues} matched ·{' '}
              {preview.already_expired} already expired (history only) · {preview.duplicates_in_file} duplicates in file ·{' '}
              {preview.skipped_count} unreadable rows skipped
            </p>
            <div className="space-y-1.5">
              {preview.venues.map((v) => (
                <div key={v.norm} className="flex items-center gap-2 text-xs" data-testid={`history-venue-${v.norm}`}>
                  <span className="text-white font-medium truncate max-w-[220px]" title={v.name}>{v.name}</span>
                  <span className="text-[#8892b0]">({v.rows} rows)</span>
                  {v.via === 'name' && <span className="text-[#4ade80]">→ {v.venue_name}</span>}
                  {v.via === 'mapping' && <span className="text-[#4ade80]">→ {v.venue_name} (remembered)</span>}
                  {v.via === 'skipped' && <span className="text-[#8892b0] italic">skipped</span>}
                  {v.via === null && (
                    <select data-testid={`history-map-${v.norm}`} defaultValue=""
                      onChange={(e) => mapVenue(v.name, e.target.value === '__skip__' ? null : e.target.value)}
                      className="bg-[#000e2a] border border-[#ef4444]/40 text-white text-xs rounded px-2 py-1">
                      <option value="" disabled>Unmatched — pick a venue…</option>
                      {venues.map((cv) => <option key={cv.venue_id} value={cv.venue_id}>{cv.venue_name}</option>)}
                      <option value="__skip__">Skip this venue</option>
                    </select>
                  )}
                </div>
              ))}
            </div>
            {preview.skipped_rows.length > 0 && (
              <div className="max-h-20 overflow-y-auto">
                {preview.skipped_rows.map((s, i) => (
                  <p key={i} className="text-[11px] text-[#ef4444]">row {s.row}: {s.reason}</p>
                ))}
              </div>
            )}
            <Button data-testid="history-confirm" onClick={confirm} disabled={busy || unmatched.length > 0}
              className="bg-[#22c55e]/15 text-[#22c55e] hover:bg-[#22c55e]/25 border border-[#22c55e]/30 h-9 text-xs font-bold">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
              {unmatched.length > 0 ? `Map or skip ${unmatched.length} venue(s) first` : 'Confirm import'}
            </Button>
          </div>
        )}

        {result && (
          <div data-testid="history-result" className="rounded-lg bg-[rgba(20,27,80,0.5)] border border-[#22c55e]/20 p-4 space-y-2">
            <p className="text-sm font-bold text-[#4ade80]">Import done</p>
            <p className="text-xs text-[#c8d0e8]" data-testid="history-result-stats">
              {result.written} written · {result.already_there} already there · {result.locks_applied} rounds locked ·{' '}
              {result.kept_released} kept released · {result.history_only_expired} expired (history only) ·{' '}
              {result.skipped.unmatched_venue_rows + result.skipped.bad_rows + result.skipped.duplicates_in_file} skipped
            </p>
            {Object.entries(result.per_venue).map(([name, c]) => (
              <p key={name} className="text-[11px] text-[#8892b0]">
                {name}: {c.written} written, {c.locked} locked{c.kept_released ? `, ${c.kept_released} kept released` : ''}
              </p>
            ))}
            <Button data-testid="history-undo-latest" onClick={() => undo(result.import_id)}
              className="bg-[#ef4444]/15 text-[#ef4444] hover:bg-[#ef4444]/25 border border-[#ef4444]/30 h-8 text-xs">
              <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Undo this import
            </Button>
          </div>
        )}

        {imports.length > 0 && (
          <div className="space-y-1" data-testid="history-imports-list">
            <p className="text-[11px] font-mono uppercase tracking-widest text-[#8892b0]">Recent imports</p>
            {imports.map((im) => (
              <div key={im.id} className="flex items-center gap-3 text-xs text-[#c8d0e8]">
                <span>{new Date(im.at).toLocaleString()}</span>
                <span className="text-[#8892b0]">{im.written} written · {im.locks_applied} locked</span>
                {im.undone_at ? <span className="text-[#8892b0] italic">undone</span> : (
                  <button data-testid={`history-undo-${im.id}`} onClick={() => undo(im.id)}
                    className="text-[#ef4444] hover:underline">undo</button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
